import { useSuspenseQuery } from "@tanstack/react-query";
import { differenceInMonths } from "date-fns";
import { useState } from "react";

import { ChartLegend } from "@/components/Charts/ChartLegend";
import { dataColors } from "@/components/Charts/theme";
import { parseChartDate } from "@/components/Charts/util";
import { formatDate } from "@/components/FormattedDate/FormattedDate";
import { NoData } from "@/components/NoData/NoData";
import { useCurrentDate } from "@/hooks/useCurrentDate";
import { useQueryParams } from "@/hooks/useQueryParam";
import { LineChart } from "@/modules/analytics/components/LineChart";
import type { AnalyticsRouteParams } from "@/modules/analytics/routes";
import type { DateRangeOption } from "@/modules/analytics/util";
import { filterDataByDateRange } from "@/modules/analytics/util";
import type { DateRange } from "@/queries/analytics/queryOptions";
import { useAnalyticsQueries } from "@/queries/analytics/queryOptions";
import { useTranslation } from "@/translations";
import type { NonEmptyArray } from "@/types/utility-types";

import { AnalyticsCard } from "../../../components/AnalyticsCard";

interface Props {
  dateRangeOptions: NonEmptyArray<DateRangeOption>;
}

const DEFAULT_DATE_RANGE: DateRange = "lastYear";

export function OneOnOneCommunicationsCard({ dateRangeOptions }: Props): React.ReactNode {
  const clockDate = useCurrentDate();
  const [selectedDateRange, setSelectedDateRange] = useState<DateRangeOption>(dateRangeOptions[0]);

  const { t, i18n } = useTranslation();
  const [queryParams, setQueryParams] = useQueryParams<AnalyticsRouteParams["overview"]>();
  const analyticsQueries = useAnalyticsQueries();

  const { data } = useSuspenseQuery(analyticsQueries.conversationsMonthly(DEFAULT_DATE_RANGE));

  const handleSelectDateRange = (item: DateRangeOption) => {
    setSelectedDateRange(item);

    // Remove the date from the query params when the selected date is changed manually
    setQueryParams({
      ...queryParams,
      "esg-date-range": undefined,
    } satisfies AnalyticsRouteParams["overview"]);
  };

  let selectedDateRangeOverride = selectedDateRange;
  if (queryParams["esg-date-range"]) {
    const dateNow = clockDate;
    const dateFromQuery = new Date(queryParams["esg-date-range"]);

    if (differenceInMonths(dateNow, dateFromQuery) <= 6) {
      selectedDateRangeOverride = dateRangeOptions[0];
    } else if (differenceInMonths(dateNow, dateFromQuery) <= 12) {
      selectedDateRangeOverride = dateRangeOptions[1];
    }
  }

  const mappedData =
    filterDataByDateRange(data.data, selectedDateRangeOverride).map((x) => ({
      x: parseChartDate(x.range).toISOString(),
      y: x.count,
      isPending: x.isCurrentMonth,
    })) || [];

  const noData = data.data.every((x) => x.count === 0);

  return (
    <AnalyticsCard.Root variant="graph">
      <AnalyticsCard.Header
        title={t("page.analytics-overview.chart.conversations-started.title")}
        tooltip={t("page.analytics-overview.chart.conversations-started.title-tooltip")}
      >
        <AnalyticsCard.FilterSelect
          items={dateRangeOptions}
          selected={selectedDateRangeOverride}
          onChange={handleSelectDateRange}
          renderOption={(x) => x.name}
          keySelector={(x) => x.value}
        />
      </AnalyticsCard.Header>
      {noData ? (
        <NoData />
      ) : (
        <>
          <LineChart
            key={selectedDateRangeOverride.value}
            data={[{ dataPoints: mappedData, color: dataColors.primary, label: "conversations-started" }]}
            maxY={5}
            yLabel={t("page.analytics-overview.chart.conversations-started.y-axis-label")}
            hoverLabel={({ x, y, relatedPoints }) => {
              const isPending = relatedPoints[0]?.dataPoint?.isPending;

              return (
                <>
                  <span>{formatDate(i18n, "monthYear", x)}</span>
                  <br />
                  <span>
                    {t("page.analytics-overview.chart.conversations-started.datapoint-label", {
                      amount: y,
                    })}
                  </span>
                  {isPending && (
                    <>
                      <br />
                      <span>{t("page.analytics-details.engagement.chart.tooltip.data-collection-in-progress")}</span>
                    </>
                  )}
                </>
              );
            }}
            xAxisType="date"
          />
          <ChartLegend
            items={[
              {
                label: t("page.analytics-overview.chart.conversations-started.title"),
                color: dataColors.primary,
              },
            ]}
          />
        </>
      )}
    </AnalyticsCard.Root>
  );
}
